import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "va_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export function formatError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export async function adminFetch(path, options = {}) {
  const res = await fetch(`${API}/api${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    clearToken();
    if (!window.location.pathname.endsWith("/admin/login")) window.location.href = "/admin/login";
    throw new Error("Session expired. Please log in again.");
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(formatError(data.detail));
  }
  return res.status === 204 ? null : res.json();
}

export async function adminLogin(email, password) {
  const res = await fetch(`${API}/api/admin/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatError(data.detail));
  setToken(data.token);
  return data;
}

export async function adminUpload(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API}/api/admin/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  });
  if (res.status === 401) {
    clearToken();
    if (!window.location.pathname.endsWith("/admin/login")) window.location.href = "/admin/login";
    throw new Error("Session expired. Please log in again.");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatError(data.detail));
  return data; // { url, path }
}

// Resolve a stored achiever photo value to a displayable src.
// - "/api/..." → backend-served upload (prepend backend URL)
// - "/achievers/..." static or full http(s) → use as-is
export function photoSrc(photo) {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${API}${photo}`;
  return photo;
}

export function AdminProtected({ children }) {
  // Initialise from token presence so we don't call setState synchronously inside the effect.
  const [state, setState] = useState(() => (getToken() ? "checking" : "no"));
  useEffect(() => {
    if (!getToken()) return undefined;
    let active = true;
    adminFetch("/admin/me")
      .then(() => { if (active) setState("yes"); })
      .catch(() => { if (active) setState("no"); });
    return () => { active = false; };
  }, []);

  if (state === "checking")
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 border-4 border-vedanti-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (state === "no") return <Navigate to="/admin/login" replace />;
  return children;
}
